import { AssetGroup } from '@play-co/timestep-core';
import { ImageScaleView, ImageView, View } from '@play-co/timestep-core/ui';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import { locale } from 'src/StateObserver';
import { Feature } from 'src/replicant/analytics';

/**
 * Close handler
 *
 * If needSupport is true, the user should be redirected to the support page
 */
type CloseHandler = (needSupport: boolean) => void;
type PopupOpts = { superview: View; close: CloseHandler };
type FailurePopupConfig = {
  title: string;
  message: string;
  action: () => string;
  note?: () => string;
  height: number;
  offsetY: number;
  actionY: number;
  messageLayout: {
    x: number;
    y: number;
    width: number;
    height: number;
    size: number;
  };
};

const POPUP_WIDTH = 558;
const SCRIM_COLOR = 'rgba(71,24,67,0.7)';

/** Japanese Figma geometry shared by the failed and fatal variants. */
const layout = {
  frame: { x: 33, y: 86, width: 499, height: 203 },
  icon: { x: 224, y: 97, width: 113, height: 113 },
  message: { x: 50, y: 219, width: 464, height: 43, size: 36 },
  compactMessage: { x: 50, y: 211, width: 464, height: 72, size: 26 },
  note: { x: 82, y: 313, width: 394, height: 58 },
  action: { x: 57, width: 444, height: 82 },
  actionLabelOffsetX: 1,
} as const;

const variantLayout = {
  failed: { height: 506, offsetY: 52, actionY: 391 },
  fatal: { height: 428, offsetY: 13, actionY: 313 },
} as const;

/**
 * Base popup for a failed purchase
 *
 * @event close: CloseHandler - If needSupport is true, the user should be redirected to the support page
 */
abstract class PopupPurchaseFailure extends PopupBasicLazy {
  /**
   * Required assets for this popup
   */
  private assets = {
    whiteFrame: 'assets/ui/shared/frames/white_frame.png',
    failed: 'assets/ui/shop/icons/purchase_failed.png',
  };

  /** Localized title and message supplied by the concrete popup. */
  private readonly content: Pick<FailurePopupConfig, 'title' | 'message'>;

  /**
   * Failed purchase icon
   */
  private icon: ImageView;

  /**
   * Contact support button
   */
  private actionButton: ButtonScaleViewWithText;

  /**
   * Load assets on demand
   */
  protected getAssetsGroup(): AssetGroup {
    return new AssetGroup(Object.values(this.assets));
  }

  /**
   * Popup constructor
   */
  protected constructor(opts: PopupOpts, config: FailurePopupConfig) {
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      zIndex: 10000,
      width: POPUP_WIDTH,
      height: config.height,
      offsetY: config.offsetY,
      darkerBg: true,
      tracking: {
        feature: Feature.IAP._,
        $subFeature: Feature.IAP.POPUP_CANCEL_PURCHASE._,
      },
    });

    this.content = { title: config.title, message: config.message };

    this.bg.updateOpts({ backgroundColor: SCRIM_COLOR });
    this.title.updateOpts({ size: 30 });
    this.buttonClose.updateOpts({ x: 539, y: 7 });

    new ImageScaleView({
      superview: this.box,
      zIndex: 0,
      image: this.assets.whiteFrame,
      ...layout.frame,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, center: 10, right: 30 },
        vertical: { top: 30, middle: 10, bottom: 30 },
      },
    });

    this.icon = new ImageView({
      superview: this.box,
      zIndex: 1,
      image: this.assets.failed,
      ...layout.icon,
    });

    this.message.updateOpts({
      ...config.messageLayout,
      zIndex: 2,
      align: 'center',
      verticalAlign: 'center',
      color: '#9C693A',
      font: bitmapFonts('Body'),
      wordWrap: true,
      isRichText: false,
    });

    if (config.note !== undefined) {
      const { note } = config;

      new LangBitmapFontTextView({
        superview: this.box,
        ...layout.note,
        zIndex: 1,
        align: 'center',
        verticalAlign: 'center',
        size: 24,
        color: '#9C693A',
        font: bitmapFonts('Body'),
        wordWrap: true,
        localeText: note,
        canHandleEvents: false,
      });
    }

    this.actionButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      ...layout.action,
      y: config.actionY,
      zIndex: 1,
      labelOffsetX: layout.actionLabelOffsetX,
      labelOffsetY: 0,
      fontSize: 36,
      font: bitmapFonts('BodyBig'),
      localeText: config.action,
      onClick: async () => opts.close(true),
      tracking: {
        feature: Feature.IAP._,
        $subFeature: Feature.IAP.POPUP_CANCEL_PURCHASE.SUPPORT,
      },
    });
  }

  /**
   * Show the popup with the correct failure message
   */
  public init(_opts: {}): void {
    super.init(this.content);
    this.icon.updateOpts({ image: this.assets.failed });
  }
}

/**
 * Popup for a failed purchase
 *
 * @example
 *
 * const needSupport = await openPopupPromise('popupPurchaseFailed', {});
 *
 * if (needSupport) openSupportUI();
 */
export default class PopupPurchaseFailed extends PopupPurchaseFailure {
  constructor(opts: PopupOpts) {
    super(opts, {
      ...variantLayout.failed,
      title: i18n('purchaseFailed.failed.title'),
      message: i18n('purchaseFailed.failed.message'),
      action: () => i18n('purchaseFailed.failed.action'),
      note: () => i18n('purchaseFailed.failed.note'),
      messageLayout: {
        ...layout.message,
        size: locale() === 'ja' ? 36 : 32,
      },
    });
  }
}

/**
 * Popup for a fatal purchase failure
 *
 * @example
 *
 * const needSupport = await openPopupPromise('popupPurchaseFatal', {});
 *
 * if (needSupport) openSupportUI();
 */
export class PopupPurchaseFatal extends PopupPurchaseFailure {
  constructor(opts: PopupOpts) {
    super(opts, {
      ...variantLayout.fatal,
      title: i18n('purchaseFailed.fatal.title'),
      message: i18n('purchaseFailed.fatal.message'),
      action: () => i18n('purchaseFailed.fatal.action'),
      messageLayout: layout.compactMessage,
    });
  }
}
